import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import SignatureManager from "../../components/SignatureManager";
import ReviewManager from "../../components/ReviewManager";

const SinCard = () => {
  const navigate = useNavigate();

  // ✅ EXACT SAME AS HEALTH CARD
  const { id: paramId } = useParams();

  const loggedInId = localStorage.getItem("userId");
  const userRole = localStorage.getItem("userRole");
  const isAdmin = userRole === "1";

  const userId = isAdmin && paramId ? paramId : loggedInId;

  const [isSigned, setIsSigned] = useState(false);
  const [file, setFile] = useState(null);
  const [sinNumber, setSinNumber] = useState("");
  const [expiryDate, setExpiryDate] = useState("");
  const [uploadedFileName, setUploadedFileName] = useState(null);
  const [originalFileName, setOriginalFileName] = useState(null);
  const [createdDate, setCreatedDate] = useState(null);

  const API_BASE = process.env.REACT_APP_API_BASE_URL;

  const fetchSinCard = useCallback(async () => {
    if (!userId) return;

    try {
      const res = await axios.get(`${API_BASE}/api/sincard/${userId}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });

      setUploadedFileName(res.data.sin_file || null);
      setOriginalFileName(res.data.sin_original || null);
      setSinNumber(res.data.sin_number || "");
      setExpiryDate(
        res.data.sin_expiry_date
          ? new Date(res.data.sin_expiry_date).toISOString().split("T")[0]
          : ""
      );
      setCreatedDate(res.data.sin_created_date || null);
    } catch {
      resetForm();
    }
  }, [API_BASE, userId]);

  const resetForm = () => {
    setUploadedFileName(null);
    setOriginalFileName(null);
    setSinNumber("");
    setExpiryDate("");
    setCreatedDate(null);
  };

  useEffect(() => {
    fetchSinCard();
  }, [fetchSinCard]);

  useEffect(() => {
    const fetchTaskStatus = async () => {
      const res = await axios.get(
        `${API_BASE}/api/usertasks/status/${userId}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      const TASK_ID = 56;
      const status =
        res.data.status?.[String(TASK_ID)] ??
        res.data.status?.["ssn_card"] ??
        "not_completed";

      setIsSigned(status === "review" || status === "completed");
    };

    if (userId) fetchTaskStatus();
  }, [API_BASE, userId]);

  const handleFileChange = (e) => {
    const selected = e.target.files[0];
    if (!selected) return;

    const allowedTypes = ["application/pdf", "image/jpeg", "image/png"];
    if (!allowedTypes.includes(selected.type)) {
      alert("Only PDF, JPG, or PNG files are allowed!");
      return;
    }
    setFile(selected);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const formData = new FormData();
    formData.append("userId", userId);
    formData.append("sin_number", sinNumber);
    formData.append("sin_expiry_date", expiryDate || "");
    if (file) formData.append("file", file);

    const response = await axios.post(
      `${API_BASE}/api/sincard/upload?userId=${userId}`,
      formData,
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          "Content-Type": "multipart/form-data",
        },
      }
    );

    alert(response.data.message);
    fetchSinCard();
  };

  return (
    <>
      <div className="d-flex justify-content-between align-items-center pt-3">
        <div className="d-flex align-items-center">
          <span className="me-2 text-danger"></span>
          <span style={{ fontSize: "18px", fontWeight: "500" }}>SSN Card</span>
        </div>
        <button className="btn btn-secondary" onClick={() => navigate(-1)}>
          Return
        </button>
      </div>

      <div className="form-wrapper my-4">
        <div className="row align-items-center mb-4">
          <div className="col-4"></div>
          <div className="col-4 text-center">
            <h3 className="mb-0">SSN Card</h3>
          </div>
          <div className="col-4 text-end">
            <div>New-Way Trucking Ltd</div>
            <div>7725 48 St. SE, Calgary</div>
            <div>Alberta, Canada T2C 2V3</div>
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="row">
            <div className="col-md-6 mb-3">
              <label>SSN Number</label>
              <input
                className="form-control"
                value={sinNumber}
                onChange={(e) => setSinNumber(e.target.value)}
                disabled={!isAdmin && isSigned}
              />
            </div>

            <div className="col-md-6 mb-3">
              <label>Expiry Date</label>
              <input
                type="date"
                className="form-control"
                value={expiryDate}
                onChange={(e) => setExpiryDate(e.target.value)}
                disabled={!isAdmin && isSigned}
              />
            </div>

            <div className="col-md-12 mb-3">
              <label>Select File (PDF / JPG / PNG)</label>
              <input
                type="file"
                className="form-control"
                onChange={handleFileChange}
                disabled={!isAdmin && isSigned}
              />
            </div>
          </div>

          {uploadedFileName && (
            <div className="p-3 bg-light rounded border">
              <div className="text-muted mb-2">Uploaded Version</div>
              <a
                href={`${API_BASE}/uploads/sincards/${uploadedFileName}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                {originalFileName}
              </a>
            </div>
          )}

          <div className="mt-4 d-flex justify-content-end">
            <button
              type="submit"
              className="btn btn-primary"
              disabled={!isAdmin && isSigned}
            >
              Save
            </button>
          </div>
        </form>

        <hr className="my-5" />

        {userId && (
          <>
            <SignatureManager
              taskName="ssn_card"
              taskId={56}
              userId={isAdmin && paramId ? paramId : userId}
              isAdmin={isAdmin}
            />
            <ReviewManager
              taskName="ssn_card"
              taskId={56}
              userId={isAdmin && paramId ? paramId : userId}
              isAdmin={isAdmin}
            />
          </>
        )}
      </div>
    </>
  );
};

export default SinCard;
